import React from 'react';
import { Shield, Target, Heart, Award, CheckCircle } from 'lucide-react';

export const About = () => {
  return (
    <div className="pt-20">
      {/* Hero Section with Background Image */}
      <section className="relative bg-gradient-to-br from-blue-600 to-blue-700 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1486551937199-baf066858de7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMHBhdHRlcm5zfGVufDB8fHx8MTc2MjU0OTAwMnww&ixlib=rb-4.1.0&q=85" 
            alt="Background" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-6xl font-bold mb-6">About Us</h1>
            <p className="text-2xl text-white/95">
              Your trusted partner in intellectual property protection and brand security
            </p>
          </div>
        </div>
      </section>

      {/* Our Story with Image */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-5xl font-bold text-gray-900 mb-8">Our Story</h2>
                <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                  <p>
                    At Trademark Frontline, we understand that your brand is more than just a name or logo—it's the culmination of your vision, hard work, and dedication. Our mission is to provide comprehensive trademark and intellectual property services that protect what matters most to your business.
                  </p>
                  <p>
                    We've built our reputation on delivering exceptional service, expert guidance, and reliable protection for businesses of all sizes. From startups launching their first product to established enterprises expanding their brand portfolio, we bring the same level of commitment and expertise to every client relationship.
                  </p>
                  <p>
                    Our team combines deep knowledge of trademark law with a client-first approach, ensuring that you receive personalized attention and strategic advice tailored to your unique business needs.
                  </p>
                </div>
              </div>
              <div className="relative">
                <div className="rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://images.unsplash.com/photo-1497366754035-f200968a6e72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMG9mZmljZXxlbnwwfHx8fDE3NjI1NTM5MTF8MA&ixlib=rb-4.1.0&q=85" 
                    alt="Professional office environment" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-primary text-white p-8 rounded-xl shadow-xl">
                  <div className="text-4xl font-bold mb-2">15+</div>
                  <div className="text-sm">Years of Excellence</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business Excellence Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="relative rounded-2xl overflow-hidden shadow-xl h-80">
                <img 
                  src="https://images.unsplash.com/photo-1593062096033-9a26b09da705?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHx3b3Jrc3BhY2V8ZW58MHx8fHwxNzYyNTUzOTE2fDA&ixlib=rb-4.1.0&q=85" 
                  alt="Professional workspace" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-6">
                  <h3 className="text-white text-xl font-bold">Modern Workspace</h3>
                </div>
              </div>
              <div className="relative rounded-2xl overflow-hidden shadow-xl h-80">
                <img 
                  src="https://images.unsplash.com/photo-1499951360447-b19be8fe80f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwzfHx3b3Jrc3BhY2V8ZW58MHx8fHwxNzYyNTUzOTE2fDA&ixlib=rb-4.1.0&q=85" 
                  alt="Tech environment" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-6">
                  <h3 className="text-white text-xl font-bold">Technology Driven</h3>
                </div>
              </div>
              <div className="relative rounded-2xl overflow-hidden shadow-xl h-80">
                <img 
                  src="https://images.unsplash.com/photo-1497366811353-6870744d04b2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHw0fHx3b3Jrc3BhY2V8ZW58MHx8fHwxNzYyNTUzOTE2fDA&ixlib=rb-4.1.0&q=85" 
                  alt="Meeting space" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-6">
                  <h3 className="text-white text-xl font-bold">Professional Setting</h3>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-5xl font-bold text-gray-900 mb-16 text-center">Our Core Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 max-w-7xl mx-auto">
            <div className="bg-gradient-to-br from-red-50 to-white rounded-2xl p-10 shadow-lg text-center hover:shadow-2xl transition-shadow">
              <div className="bg-blue-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="text-blue-600" size={36} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Protection First</h3>
              <p className="text-gray-600 leading-relaxed">
                We prioritize securing your intellectual property with the highest level of diligence and care
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-orange-50 to-white rounded-2xl p-10 shadow-lg text-center hover:shadow-2xl transition-shadow">
              <div className="bg-orange-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Target className="text-orange-600" size={36} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Precision & Accuracy</h3>
              <p className="text-gray-600 leading-relaxed">
                Every detail matters when it comes to trademark registration—we ensure perfection in every filing
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-10 shadow-lg text-center hover:shadow-2xl transition-shadow">
              <div className="bg-blue-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart className="text-blue-600" size={36} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Client-Centered</h3>
              <p className="text-gray-600 leading-relaxed">
                Your success is our success—we're dedicated to providing personalized service and support
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-orange-50 to-white rounded-2xl p-10 shadow-lg text-center hover:shadow-2xl transition-shadow">
              <div className="bg-orange-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Award className="text-orange-600" size={36} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Excellence Always</h3>
              <p className="text-gray-600 leading-relaxed">
                We maintain the highest standards in every aspect of our service delivery and client interactions
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-5xl font-bold text-gray-900 mb-16 text-center">Our Approach</h2>
            <div className="space-y-8">
              <div className="flex gap-6 bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="flex-shrink-0 w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-2xl">
                  1
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Understanding Your Needs</h3>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    We take time to understand your business, industry, and brand goals to provide tailored protection strategies
                  </p>
                </div>
              </div>
              
              <div className="flex gap-6 bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="flex-shrink-0 w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-2xl">
                  2
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Transparent Communication</h3>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    We keep you informed at every stage with clear explanations and regular updates on your application status
                  </p>
                </div>
              </div>
              
              <div className="flex gap-6 bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="flex-shrink-0 w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-2xl">
                  3
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Strategic Execution</h3>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    Our experienced team handles every aspect of your trademark registration with strategic precision and legal expertise
                  </p>
                </div>
              </div>
              
              <div className="flex gap-6 bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="flex-shrink-0 w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-2xl">
                  4
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Ongoing Support</h3>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    Our relationship doesn't end at registration—we provide continued support for renewals, monitoring, and enforcement
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why We're Different with Background */}
      <section className="relative py-24 text-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-blue-700"></div>
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdHxlbnwwfHx8fDE3NjI1NTM5MjF8MA&ixlib=rb-4.1.0&q=85" 
            alt="Abstract business design" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-5xl font-bold mb-8">Why We're Different</h2>
            <p className="text-2xl mb-8 leading-relaxed text-white/95">
              Unlike generic legal services, we specialize exclusively in intellectual property protection. This focused expertise means we're always up-to-date with the latest trademark regulations, strategies, and best practices.
            </p>
            <p className="text-2xl leading-relaxed text-white/95">
              Our success is measured by your peace of mind. When you work with us, you're not just getting a service provider—you're gaining a trusted partner committed to the long-term success and protection of your brand.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};